import {
  AutoAwesomeOutlined,
  CloseOutlined,
  HealthAndSafetyOutlined,
  Phone,
} from "@mui/icons-material";
import {
  Avatar,
  Button,
  Card,
  Dialog,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Box, Container } from "@mui/system";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { LoginForm } from "components/User";
import { useAuth } from "contexts/User";
import { Queries, useLazyQuery, Mutations, useMutation, useQuery } from "services/apollo";
import RedirectToISolvedDialog from "./RedirectToISolvedDialog";
import { PostAccountSyncView } from "../LandingPageCommonComponents/PostAccountSyncView";
import { mixpanelService as mixpanel } from 'services/tracking';
import LandingCreateAccountOrLogin from "../LandingPageCommonComponents/LandingCreateAccount";
import { ISolvedActivity } from "services/Interfaces";
import { useFirebase } from "contexts/Firebase";
import { addDoc, collection } from "firebase/firestore";
import { 
  TRACKING_EVENTS, 
  buildLandingPageTrackingProps, 
  buildIdentityConfirmationProps,
  buildSyncStatusProps 
} from "utils/trackingUtils";
// import { useParams } from "react-router-dom";

interface ISolvedFormInfo {
  lastName: string;
  dob: string;
  zipCode: string;
}

export interface ISolvedUserActivity {
  activity: string;
  type: string;
  userId?: string;
  timestamp: Date;
  lastName?: string;
}

interface ISolvedLandingPageProps {
  companyId?: string;
  caseId?: string;
  exitCodeInfo?: {
    firstName: string;
    documents: number;
    whenBenefit: number;
    servicesEnabled: string[];
  } | null;
}

// const ISOLVED_COMPANY_ID = "isolved";
const syncErrorMessage =
  "An account already exists for this user. If you have already created an account, log in with your email and password. For assistance, call us at +1 (831) 228-6412.";

const ISolvedLandingPage = ({ companyId = 'isolved', caseId, exitCodeInfo: propExitCodeInfo }: ISolvedLandingPageProps) => {
  const { user } = useAuth();
  const { firestore } = useFirebase();
  // const { companyId: paramCompanyId, caseId: paramCaseId } = useParams();

  // State declarations
  const [error, setError] = useState<string>("");
  const [loginOpen, setLoginOpen] = useState<boolean>(false);
  const [signUpOpen, setSignUpOpen] = useState<boolean>(false);
  const [redirectToIsolvedDialogOpen, setRedirectToIsolvedDialogOpen] = useState<boolean>(false);
  const [infoConfirmed, setInfoConfirmed] = useState<boolean>(false);
  const [validationObject, setValidationObject] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<ISolvedFormInfo>({
    lastName: "",
    dob: "",
    zipCode: "",
  });
  const [syncingData, setSyncingData] = useState<boolean>(false);
  const [dataSynced, setDataSynced] = useState<boolean>(false);

  // GraphQL queries and mutations
  const { data: exitCodeData, loading } = useQuery(Queries.exitCodeInfo, {
    variables: {
      companyId: companyId || "",
      exitCode: caseId || companyId || "",
    },
    skip: !companyId || Boolean(propExitCodeInfo),
  });

  const [confirmExitCodeIdentity] = useLazyQuery(Queries.confirmExitCodeIdentity, {
    fetchPolicy: "network-only",
  });

  const [syncDataForExitCode] = useMutation(Mutations.syncDataForExitCode);

  // Use either the prop or query data
  const exitCodeInfo = propExitCodeInfo || exitCodeData?.exitCodeInfo;

  const isPACEnabled = Boolean(exitCodeInfo?.servicesEnabled?.includes('PAC'));

  // Function to set Mixpanel user profile data
  const setMixpanelUserProfile = useCallback(async () => {
    if (!companyId || !user) return;
    
    try {
      // Set Mixpanel user profile data
      mixpanel.set_when_benefit_data({
        company_code: companyId,
      });

    } catch (e) {
      console.error('Error setting Mixpanel user profile data', e);
    }
  }, [companyId, user]);

  // Activity tracking
  const addIsolvedUserActivityToRecord = useCallback(
    async (activity: ISolvedUserActivity) => {
      const userActivityCollectionRef = collection(
        firestore,
        `users/${user?.uid}/activity`
      );
      await addDoc(userActivityCollectionRef, activity);
    },
    [firestore, user?.uid]
  );

  useEffect(() => {
    const trackUserActivity = async () => {
      // Track the generic Landing Page Viewed event
      const trackingProps = buildLandingPageTrackingProps(companyId, caseId);
      mixpanel.track(TRACKING_EVENTS.LANDING_PAGE_VIEWED, trackingProps);
      
      // Still record activity in Firestore for backward compatibility
      await addIsolvedUserActivityToRecord({
        activity: ISolvedActivity.ViewedIsolvedLandingPage,
        type: "user",
        userId: user?.uid,
        timestamp: new Date(),
      });
    };

    trackUserActivity();
  }, [addIsolvedUserActivityToRecord, user?.uid, companyId, caseId]);

  // Call setMixpanelUserProfile when user is authenticated and data is synced
  useEffect(() => {
    if (infoConfirmed && dataSynced && user) {
      setMixpanelUserProfile();
    }
  }, [infoConfirmed, dataSynced, user, setMixpanelUserProfile]);

  useEffect(() => {
    if (!companyId) {
      return;
    }

    if (
      infoConfirmed &&
      !dataSynced &&
      !syncingData &&
      user &&
      user.uid &&
      !user.isAnonymous
    ) {
      // Identify the user in Mixpanel
      mixpanel.identify(user.uid);
      
      setSyncingData(true);
      setTimeout(async () => {
        try {
          const result = await syncDataForExitCode({
            variables: {
              companyId,
              exitCode: caseId || "",
              lastName: formData?.lastName,
              birthDate: formData?.dob,
              zipCode: formData?.zipCode,
            },
          });
          if (result.data?.syncDataForExitCode) {
            // Track successful sync
            const syncProps = buildSyncStatusProps(true, caseId || undefined, user.uid);
            mixpanel.track(TRACKING_EVENTS.SYNC_STATUS, syncProps);
            
            setSyncingData(false);
            setDataSynced(true);
            // After successful sync, set Mixpanel user profile data
            setMixpanelUserProfile();
          } else {
            // Track failed sync
            const syncProps = buildSyncStatusProps(false, caseId || undefined, user.uid);
            mixpanel.track(TRACKING_EVENTS.SYNC_STATUS, syncProps);
            
            setError("Error syncing data");
          }
        } catch (e) {
          const error = e as Error;
          
          // Track failed sync
          const syncProps = buildSyncStatusProps(false, caseId || undefined, user.uid, { 
            error: error.message 
          });
          mixpanel.track(TRACKING_EVENTS.SYNC_STATUS, syncProps);
          
          if (error.message === "Case has already been synchronized") {
            setError(syncErrorMessage);
          } else {
            setError("Something went wrong syncing data");
          }
          console.error(e);
        }
      }, 500);
    }
  }, [
    infoConfirmed,
    dataSynced,
    syncDataForExitCode,
    companyId,
    caseId,
    syncingData,
    formData,
    user,
    user?.isAnonymous,
    isPACEnabled,
    setMixpanelUserProfile,
  ]);

  // Don't render anything while loading
  if (loading) {
    return null;
  }

  async function afterLogin() {
    setLoginOpen(false);
  }

  const confirmIdentity = async () => {
    // Track identity confirmation attempt with new generic event
    const trackingProps = buildIdentityConfirmationProps(
      false, // Not confirmed yet, this is just the attempt
      companyId,
      caseId,
      { lastName: formData?.lastName }
    );
    mixpanel.track(TRACKING_EVENTS.IDENTITY_CONFIRMATION, trackingProps);
    
    // Still record activity in Firestore for backward compatibility
    await addIsolvedUserActivityToRecord({
      activity: ISolvedActivity.IsolvedUserValidationAttempted,
      type: "user",
      userId: user?.uid,
      timestamp: new Date(),
      lastName: formData?.lastName,
    });
    // check validations
    const validation: any = {};
    Object.keys(formData).forEach(function (key) {
      if (!formData[key as keyof ISolvedFormInfo]) {
        validation[key] = `${key?.charAt(0).toUpperCase()}${key?.slice(
          1
        )} cannot be empty`;
      } else if (
        key === "zipCode" &&
        !formData[key as keyof ISolvedFormInfo]?.match(
          "(^\\d{5}$)|(^\\d{9}$)|(^\\d{5}-\\d{4}$)"
        )
      ) {
        validation[key] = `Invalid ${key?.charAt(0).toUpperCase()}${key?.slice(
          1
        )}`;
      }
    });
    if (Object.keys(validation).length > 0) {
      setValidationObject(validation);
      return;
    }
    if (!companyId) {
      setError("Invalid URL");
      return;
    }
    try {
      const result = await confirmExitCodeIdentity({
        variables: {
          companyId,
          exitCode: isPACEnabled ? companyId : (caseId || ""),
          lastName: formData?.lastName,
          birthDate: formData?.dob,
          zipCode: formData?.zipCode,
          workEmail: "",
        },
        fetchPolicy: "network-only",
      });
      if (result.error) {
        console.error(result.error);
        setError(result.error.message);
        return;
      }
      if (result.data?.confirmExitCodeIdentity) {
        // Track successful identity confirmation with new generic event
        const successTrackingProps = buildIdentityConfirmationProps(
          true, // Identity confirmed successfully
          companyId,
          caseId,
          { lastName: formData?.lastName }
        );
        mixpanel.track(TRACKING_EVENTS.IDENTITY_CONFIRMATION, successTrackingProps);
        
        // Still record activity in Firestore for backward compatibility
        await addIsolvedUserActivityToRecord({
          activity: ISolvedActivity.IsolvedUserValidationSuccessful,
          type: "user",
          userId: user?.uid,
          timestamp: new Date(),
        });
        
        setInfoConfirmed(true);
      } else {
        // No error BUT case not found, will need to redirect to isolved and save previously answered questions in user doc

        setRedirectToIsolvedDialogOpen(true);
      }
    } catch (e: any) {
      console.error(e);
      setError(e.message);
    }
  };

  const handleChangeForm =
    (name: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData({ ...formData, [name]: event.target.value });
      if (!event.target.value) {
        setValidationObject({
          ...validationObject,
          [name]: `${name?.charAt(0).toUpperCase()}${name?.slice(
            1
          )} cannot be empty`,
        });
      } else {
        delete validationObject[name];
        setValidationObject(validationObject);
      }
      if (!event.target.value && name !== "zipCode") {
        setValidationObject({
          ...validationObject,
          [name]: `${name?.charAt(0).toUpperCase()}${name?.slice(
            1
          )} cannot be empty`,
        });
      } else if (
        name === "zipCode" &&
        !event.target.value.match("(^\\d{5}$)|(^\\d{9}$)|(^\\d{5}-\\d{4}$)")
      ) {
        setValidationObject({
          ...validationObject,
          [name]: `Invalid ${name?.charAt(0).toUpperCase()}${name?.slice(1)}`,
        });
      } else {
        delete validationObject[name];
        setValidationObject(validationObject);
      }
    };
  const handleDateChange = (name: string, date: string) => {
    setFormData({ ...formData, [name]: date });
    if (name === "dob") delete validationObject["dob"];
    setValidationObject(validationObject);
  };

  const renderContent = () => {
    // Only render content if we have exitCodeInfo
    if (!exitCodeInfo?.servicesEnabled) {
      // console.log('Waiting for services data...');
      return null;
    }

    const hasAAS = exitCodeInfo.servicesEnabled.includes('AAS');

    if (hasAAS) {
      return (
        <>
          <Typography
            sx={{
              mt: "30px",
              fontSize: { xs: "0.875rem", sm: "1rem", md: "1.25rem" },
            }}
            variant="body1"
            color="white.main"
            align="center"
          >
            isolved Benefits Services has teamed up with When to help you understand your health insurance options beyond COBRA, empowering you to make the best decision for you and your family&apos;s well-being.
          </Typography>
          <Typography
            sx={{
              mt: "30px",
              fontSize: { xs: "0.875rem", sm: "1rem", md: "1.25rem" },
            }}
            variant="body1"
            color="white.main"
            align="center"
          >
            When&apos;s AI-enabled tools let you instantly compare recommended plans that may be more affordable or better suited to your needs.
          </Typography>
          <Typography
            sx={{
              mt: "30px",
              fontSize: { xs: "0.875rem", sm: "1rem", md: "1.25rem" },
            }}
            variant="body1"
            color="white.main"
            align="center"
          >
            The Alumni Advisory program delivers a proactive, personalized experience through dedicated one-on-one guidance. Our licensed agents are ready to answer your questions, help you select a plan, or assist with enrollment - ensuring a smooth coverage transition.
          </Typography>
          <Typography
            sx={{
              mt: "30px",
              fontSize: { xs: "0.875rem", sm: "1rem", md: "1.25rem" },
            }}
            variant="body1"
            color="white.main"
            align="center"
          >
            This collaboration furthers our commitment to providing compassionate, tailored assistance during career transitions, making a potentially stressful process much more manageable.
          </Typography>
        </>
      );
    }

    if (exitCodeInfo.servicesEnabled.includes('PAC')) {
      return (
        <>
          <Typography
            sx={{
              mt: "30px",
              fontSize: { xs: "0.875rem", sm: "1rem", md: "1.25rem" },
            }}
            variant="body1"
            color="white.main"
            align="center"
          >
            isolved Benefits Services has partnered with When to provide you with Portable Alternative Coverage (PAC) options. This program offers you the opportunity to explore health insurance alternatives that may better suit your needs.
          </Typography>
          <Typography
            sx={{
              mt: "30px",
              fontSize: { xs: "0.875rem", sm: "1rem", md: "1.25rem" },
            }}
            variant="body1"
            color="white.main"
            align="center"
          >
            Our platform makes it easy to compare plans and find coverage that fits your lifestyle. With PAC, you can take your health insurance with you wherever you go, ensuring continuous coverage for you and your family.
          </Typography>
        </>
      );
    }

    // Default content
    return (
      <>
        <Typography
          sx={{
            mt: "30px",
            fontSize: { xs: "0.875rem", sm: "1rem", md: "1.25rem" },
          }}
          variant="body1"
          color="white.main"
          align="center"
        >
          isolved Benefits Services has partnered with When to help you explore your health insurance options. Our platform makes it easy to compare plans and find coverage that fits your needs.
        </Typography>
        <Typography
          sx={{
            mt: "30px",
            fontSize: { xs: "0.875rem", sm: "1rem", md: "1.25rem" },
          }}
          variant="body1"
          color="white.main"
          align="center"
        >
          When&apos;s AI-enabled tools and licensed agents are here to guide you through the process of finding the right health insurance plan for you and your family.
        </Typography>
      </>
    );
  };

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Box
          sx={{
            position: "relative",
            height: "100%",
            backgroundImage: `url('/isolved-banner-image.png')`,
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            display: "flex",
            flexDirection: "column",
            zIndex: 10,
          }}
        >
          <Container
            maxWidth="xl"
            sx={{ position: "relative", zIndex: 12, pt: 6 }}
          >
            <Box
              sx={{
                width: "100%",
                minHeight: "100%",
                pt: 2,
              }}
            >
              <Grid
                container
                sx={{ flexWrap: "nowrap" }}
                alignItems="center"
                justifyContent="center"
                direction="column"
              >
                <Grid
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "end",
                    px: { xs: 4, sm: 6, md: 8 },
                  }}
                  item
                >
                  <Box
                    component="img"
                    sx={{
                      height: "auto",
                      width: "auto",
                      maxHeight: { xs: 90, sm: 125, md: 175 },
                      maxWidth: { xs: 360, sm: 500, md: 700 },
                    }}
                    alt="isolved-logo"
                    src={"/isolved-logo.svg"}
                  />
                  <Typography
                    variant="h1"
                    color="white.main"
                    sx={{
                      fontSize: { xs: "1rem", sm: "1.75rem", md: "2.5rem" },
                    }}
                  >
                    <strong>
                      {!exitCodeInfo?.servicesEnabled ? (
                        ""
                      ) : exitCodeInfo.servicesEnabled.includes('AAS') ? (
                        "Alumni Advisory Services"
                      ) : exitCodeInfo.servicesEnabled.includes('PAC') ? (
                        "Portable Alternative Coverage (PAC)"
                      ) : (
                        "Health Insurance Options"
                      )}
                    </strong>
                  </Typography>
                </Grid>
                <Grid
                  item
                  alignItems="center"
                  justifyContent="center"
                  display="flex"
                  flexDirection="row"
                  gap={2}
                >
                  <Typography
                    variant="h2"
                    color="white.main"
                    sx={{ fontSize: { xs: "1rem", sm: "1.5rem", md: "2rem" } }}
                  >
                    <strong>Powered by</strong>
                  </Typography>
                  <Box
                    component="img"
                    sx={{
                      height: "auto",
                      width: "auto",
                      maxHeight: { xs: 25, sm: 35, md: 50 },
                      maxWidth: { xs: 100, sm: 140, md: 200 },
                    }}
                    alt="when"
                    src={"/when-logo-white.png"}
                  />
                </Grid>
              </Grid>
              <Grid
                container
                spacing={6}
                sx={{
                  py: {
                    xs: 4,
                    sm: 8,
                    md: 12,
                  },
                }}
              >
                <Grid item xs={12} sm={6}>
                  {renderContent()}
                  <Typography
                    sx={{
                      mt: "30px",
                    }}
                    variant="body2"
                    color="white.main"
                    align="center"
                  >
                    When is an approved partner with HealthCare.gov and appointed by multiple health insurance carriers. We&apos;re changing how people find, understand, and purchase their coverage, providing access to ACA and private medical plans in a one-stop, user-friendly platform. We deliver a uniquely personalized, transparent, and seamless experience - and it&apos;s absolutely free!
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <PostAccountSyncView
                    infoConfirmed={infoConfirmed}
                    syncingData={syncingData}
                    dataSynced={dataSynced}
                  />
                  {infoConfirmed && (!user || user.isAnonymous) && (
                    <Box
                      sx={{
                        background: "#FFF",
                        p: 3,
                        borderRadius: "10px",
                      }}
                    >
                      <LandingCreateAccountOrLogin
                        setSignUpOpen={setSignUpOpen}
                        setLoginOpen={setLoginOpen}
                      />
                    </Box>
                  )}
                  <Box
                    component="form"
                    onSubmit={(e: any) => {
                      e.preventDefault();
                      confirmIdentity();
                    }}
                    sx={{
                      display: !infoConfirmed ? "block" : "none",
                      background: "#FFF",
                      p: 3,
                      borderRadius: "10px",
                    }}
                  >
                    <Typography variant="h3" color="black.main">
                      <strong>First, let&apos;s confirm your info </strong>
                    </Typography>
                    <Typography sx={{ mt: 1 }} variant="body1">
                      We&apos;ll need to confirm your identity before we can
                      proceed.{" "}
                    </Typography>
                    <Typography variant="h6" color="black.main" sx={{ mt: 2 }}>
                      <strong>Last Name</strong>
                    </Typography>
                    <TextField
                      InputLabelProps={{ shrink: false }}
                      type={"text"}
                      error={Boolean(validationObject["lastName"])}
                      value={formData?.lastName}
                      onChange={handleChangeForm("lastName")}
                      fullWidth
                      data-testid="last-name-input"
                    />
                    {validationObject["lastName"] && (
                      <Typography
                        component={"div"}
                        color={"error"}
                        variant="caption"
                      >
                        {validationObject["lastName"]}
                      </Typography>
                    )}
                    <Typography variant="h6" color="black.main" sx={{ mt: 2 }}>
                      <strong>Date of Birth</strong>
                    </Typography>
                    <Box data-testid="dob-input">
                      <DesktopDatePicker
                        sx={{
                          width: "100%",
                        }}
                        value={formData?.dob ? moment(formData?.dob) : null}
                        format="MM/DD/YYYY"
                        maxDate={moment()}
                        onError={(newError) => {
                          setValidationObject({
                            ...validationObject,
                            dob: newError
                              ? "Please provide a date with the following format MM/DD/YYYY"
                              : "",
                          });
                        }}
                        onChange={(date) => {
                          handleDateChange(
                            "dob",
                            moment(date).format("MM/DD/YYYY")
                          );
                        }}
                      />
                    </Box>
                    {validationObject["dob"] && (
                      <Typography
                        component={"div"}
                        color={"error"}
                        variant="caption"
                      >
                        {validationObject["dob"]}
                      </Typography>
                    )}
                    <Typography variant="h6" color="black.main" sx={{ mt: 2 }}>
                      <strong>Zip Code</strong>
                    </Typography>
                    <TextField
                      variant="outlined"
                      type={"number"}
                      error={Boolean(validationObject["zipCode"])}
                      value={formData?.zipCode}
                      onChange={handleChangeForm("zipCode")}
                      fullWidth
                      data-testid="zip-code-input"
                      sx={{
                        "& input[type=number]": {
                          "-moz-appearance": "textfield",
                        },
                        "& input[type=number]::-webkit-outer-spin-button": {
                          "-webkit-appearance": "none",
                          margin: 0,
                        },
                        "& input[type=number]::-webkit-inner-spin-button": {
                          "-webkit-appearance": "none",
                          margin: 0,
                        },
                      }}
                    />
                    {validationObject["zipCode"] && (
                      <Typography
                        component={"div"}
                        color={"error"}
                        variant="caption"
                      >
                        {validationObject["zipCode"]}
                      </Typography>
                    )}
                    <Typography
                      variant="h6"
                      color="black.main"
                      sx={{ mt: 2 }}
                    ></Typography>
                    <Button
                      type="submit"
                      variant="contained"
                      color="black"
                      data-testid="confirm-identity-button"
                      sx={{
                        mt: 2,
                        color: "#FFF",
                        width: "100%",
                        borderRadius: "5px",
                        py: 1,
                      }}
                    >
                      Confirm
                    </Button>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="h6">
                        Already have an account?
                      </Typography>
                      <Button
                        onClick={() => {
                          setSignUpOpen(false);
                          setLoginOpen(true);
                        }}
                      >
                        Login
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Container>
        </Box>
      </Box>
      <>
        <Container maxWidth="xl">
          <Box sx={{ py: 10 }}>
            <Typography
              sx={{ mb: 6, color: "#000000" }}
              variant="h1"
              align="center"
            >
              <strong>Coverage That Matches Your Journey</strong>
            </Typography>
            <Grid container spacing={{ xs: 2, md: 3 }}>
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    gap: 3,
                    p: 4,
                    boxShadow: "0 3px 15px rgba(0, 0, 0, 0.2)",
                    alignItems: "left",
                    height: "100%",
                    transition: "box-shadow 0.3s",
                    ":hover": {
                      boxShadow: "0 0 8px rgba(0, 0, 0, 0.3)",
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      height: "50px",
                      width: "50px",
                      backgroundColor: "#000000",
                    }}
                    aria-label="recipe"
                  >
                    <HealthAndSafetyOutlined fontSize="large" />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" sx={{ mb: 2 }}>
                      <strong>Personalized Plan Browsing</strong>
                    </Typography>
                    <Typography variant="body1">
                      Easily explore a variety of health insurance plans and
                      tailor your search to your specific needs. Our platform
                      allows you to filter options based on what matters most to
                      you, ensuring you find a plan that fits your life
                      perfectly.
                    </Typography>
                  </Box>
                  {/* <Box sx={{ mt: "auto" }}>
                    <Button
                      color="black"
                      variant="outlined"
                      sx={{ py: 1, px: 3 }}
                    >
                      Browse Plans
                    </Button>
                  </Box> */}
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    gap: 3,
                    p: 4,
                    boxShadow: "0 3px 15px rgba(0, 0, 0, 0.2)",
                    alignItems: "left",
                    height: "100%",
                    transition: "box-shadow 0.3s",
                    ":hover": {
                      boxShadow: "0 0 8px rgba(0, 0, 0, 0.3)",
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      height: "50px",
                      width: "50px",
                      backgroundColor: "#000000",
                    }}
                    aria-label="recipe"
                  >
                    <AutoAwesomeOutlined fontSize="large" />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" sx={{ mb: 2 }}>
                      <strong>Meet Jamie, Your AI Assistant</strong>
                    </Typography>
                    <Typography variant="body1">
                      Meet Jamie, our intelligent AI chatbot. Jamie is designed
                      to understand your health insurance needs and guide you
                      towards the best options. It&apos;s like having a personal
                      advisor available at your fingertips!
                    </Typography>
                  </Box>
                  {/* <Box sx={{ mt: "auto" }}>
                    <Button
                      color="black"
                      variant="outlined"
                      sx={{ py: 1, px: 3 }}
                    >
                      Chat with Jamie
                    </Button>
                  </Box> */}
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    gap: 3,
                    p: 4,
                    boxShadow: "0 3px 15px rgba(0, 0, 0, 0.2)",
                    alignItems: "left",
                    height: "100%",
                    transition: "box-shadow 0.3s",
                    ":hover": {
                      boxShadow: "0 0 8px rgba(0, 0, 0, 0.3)",
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      height: "50px",
                      width: "50px",
                      backgroundColor: "#000000",
                    }}
                    aria-label="recipe"
                  >
                    <Phone fontSize="large" />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" sx={{ mb: 2 }}>
                      <strong>24/7 Access to Licensed Agents</strong>
                    </Typography>
                    <Typography variant="body1">
                      Our team of licensed insurance agents is available around
                      the clock. Whether you have complex questions or need
                      guidance through the insurance maze, they&apos;re just a
                      phone call away, any time of day or night.
                    </Typography>
                  </Box>
                  {/* <Box sx={{ mt: "auto" }}>
                    <Button
                      color="black"
                      variant="outlined"
                      sx={{ py: 1, px: 3 }}
                    >
                      Call Us
                    </Button>
                  </Box> */}
                </Card>
              </Grid>
            </Grid>
          </Box>
          <Dialog
            open={error !== ""}
            onClose={() => {
              setError("");
            }}
          >
            <Box
              sx={{
                p: 4,
              }}
            >
              <Typography color="error">Error: {error}</Typography>
              <Button
                onClick={() => {
                  setError("");
                }}
              >
                Close
              </Button>
            </Box>
          </Dialog>
          <Dialog open={signUpOpen}>
            <Box sx={{ textAlign: "center" }}>
              <LandingCreateAccountOrLogin
                setSignUpOpen={setSignUpOpen}
                setLoginOpen={setLoginOpen}
              />
            </Box>
          </Dialog>
          <Dialog
            open={loginOpen}
            onClose={() => setLoginOpen(false)}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
          >
            <IconButton
              onClick={() => {
                setLoginOpen(false);
              }}
              sx={{
                position: "absolute",
                top: 0,
                right: 0,
              }}
            >
              <CloseOutlined></CloseOutlined>
            </IconButton>
            <LoginForm afterLogin={afterLogin}></LoginForm>
          </Dialog>
          <RedirectToISolvedDialog
            open={redirectToIsolvedDialogOpen}
            onClose={() => setRedirectToIsolvedDialogOpen(false)}
            lastName={formData?.lastName}
            userId={user?.uid}
            addIsolvedUserActivityToRecord={addIsolvedUserActivityToRecord}
          />
        </Container>
      </>
    </>
  );
};

export default ISolvedLandingPage;
